import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Avatar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material"
import BusinessIcon from "@mui/icons-material/Business"
import PeopleIcon from "@mui/icons-material/People"
import GroupIcon from "@mui/icons-material/Group"
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar"
import FactoryIcon from "@mui/icons-material/Factory"
import CategoryIcon from "@mui/icons-material/Category"
import PaletteIcon from "@mui/icons-material/Palette"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import MenuItem from "@mui/material/MenuItem"
import UsersTab from "./UsersTab"
import RolesTab from "./RolesTab"
import CompanyTab from "./CompanyTab"
import SwatchesTab from "./SwatchesTab"
import CategoriesTab from "./CategoriesTab"
import CatalogUpdateTab from "./CatalogUpdateTab"
import CostDesignerTab from "./CostDesignerTab"
import CustomSizesTab from "./CustomSizesTab"
import StraightenIcon from "@mui/icons-material/Straighten"
import {
  getVehicles,
  postVehicle,
  patchVehicle,
  deleteVehicle,
  getTypes,
  createTaskType,
  updateTaskType,
  deleteTaskType,
  // App User Management
  getAppRoles,
  getAppUsersByRole,
  createAppUser,
  updateAppUser,
  deleteAppUser,
} from "../../helpers/api_helper"

// Floor options for sections
const FLOOR_OPTIONS = [
  { value: "Basement", label: "Basement" },
  { value: "Ground", label: "Ground Floor" },
  { value: "1", label: "Floor 1" },
  { value: "2", label: "Floor 2" },
  { value: "3", label: "Floor 3" },
  { value: "4", label: "Floor 4" },
  { value: "5", label: "Floor 5" },
]

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ flex: 1, overflow: "auto" }} {...other}>
      {value === index && <Box sx={{ pt: 1.5 }}>{children}</Box>}
    </div>
  )
}

const MuiSettingsConfiguration = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  document.title = t("Configuration") + " | FlexFlow ERP"

  const [activeTab, setActiveTab] = useState(0)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })

  // Roles state (from FlexFlow)
  const [roles, setRoles] = useState([])
  const [rolesLoading, setRolesLoading] = useState(false)
  const [selectedDriverRoleId, setSelectedDriverRoleId] = useState(null)

  // Drivers state (users from FlexFlow with driver role)
  const [drivers, setDrivers] = useState([])
  const [driversLoading, setDriversLoading] = useState(false)
  const [driverDialogOpen, setDriverDialogOpen] = useState(false)
  const [editingDriver, setEditingDriver] = useState(null)
  const [driverForm, setDriverForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    role_id: ""
  })
  const [driverSaving, setDriverSaving] = useState(false)

  // Vehicles state
  const [vehicles, setVehicles] = useState([])
  const [vehiclesLoading, setVehiclesLoading] = useState(false)
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState(null)
  const [vehicleForm, setVehicleForm] = useState({ make: "", model: "", year: "", license_plate: "" })
  const [vehicleSaving, setVehicleSaving] = useState(false)

  // Sections state
  const [sections, setSections] = useState([])
  const [sectionsLoading, setSectionsLoading] = useState(false)
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false)
  const [editingSection, setEditingSection] = useState(null)
  const [sectionForm, setSectionForm] = useState({ name: "", description: "", floor: "" })
  const [sectionSaving, setSectionSaving] = useState(false)

  // Fetch data on mount
  useEffect(() => {
    fetchRoles()
    fetchVehicles()
    fetchSections()
  }, [])

  // Fetch drivers when selectedDriverRoleId changes
  useEffect(() => {
    if (selectedDriverRoleId) {
      fetchDrivers()
    }
  }, [selectedDriverRoleId])

  // Fetch roles from FlexFlow
  const fetchRoles = async () => {
    setRolesLoading(true)
    try {
      const res = await getAppRoles()
      setRoles(res || [])
      const driverRole = res?.find(r => r.name?.toLowerCase() === "driver")
      if (driverRole) {
        setSelectedDriverRoleId(driverRole.id)
      } else if (res?.length > 0) {
        setSelectedDriverRoleId(res[0].id)
      }
    } catch (err) {
      console.error("Error fetching roles:", err)
      setSnackbar({ open: true, message: t("Error fetching roles from FlexFlow"), severity: "error" })
    }
    setRolesLoading(false)
  }

  // Fetch drivers (users with selected role) from FlexFlow
  const fetchDrivers = async () => {
    if (!selectedDriverRoleId) return
    setDriversLoading(true)
    try {
      const res = await getAppUsersByRole(selectedDriverRoleId)
      setDrivers(res || [])
    } catch (err) {
      console.error("Error fetching drivers:", err)
      setSnackbar({ open: true, message: t("Error fetching users from FlexFlow"), severity: "error" })
    }
    setDriversLoading(false)
  }

  const handleOpenDriverDialog = (driver = null) => {
    if (driver) {
      setEditingDriver(driver)
      const userRoles = driver.user_to_app_to_role_by_user_id || []
      const currentRoleId = userRoles.length > 0 ? userRoles[0].role_id : selectedDriverRoleId
      setDriverForm({
        first_name: driver.first_name || "",
        last_name: driver.last_name || "",
        email: driver.email || "",
        phone: driver.phone || "",
        password: "",
        role_id: currentRoleId || ""
      })
    } else {
      setEditingDriver(null)
      setDriverForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        password: "",
        role_id: selectedDriverRoleId || ""
      })
    }
    setDriverDialogOpen(true)
  }

  const handleCloseDriverDialog = () => {
    setDriverDialogOpen(false)
    setEditingDriver(null)
    setDriverForm({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      role_id: ""
    })
  }

  const handleSaveDriver = async () => {
    if (!driverForm.first_name.trim()) {
      setSnackbar({ open: true, message: t("First name is required"), severity: "error" })
      return
    }
    if (!driverForm.email.trim()) {
      setSnackbar({ open: true, message: t("Email is required"), severity: "error" })
      return
    }
    if (!driverForm.role_id) {
      setSnackbar({ open: true, message: t("Role is required"), severity: "error" })
      return
    }
    if (!editingDriver && !driverForm.password.trim()) {
      setSnackbar({ open: true, message: t("Password is required for new user"), severity: "error" })
      return
    }

    setDriverSaving(true)
    try {
      if (editingDriver) {
        const userData = {
          name: `${driverForm.first_name.trim()} ${driverForm.last_name.trim()}`.trim(),
          first_name: driverForm.first_name.trim(),
          last_name: driverForm.last_name.trim(),
          email: driverForm.email.trim(),
          phone: driverForm.phone.trim(),
        }
        if (driverForm.password.trim()) {
          userData.password = driverForm.password.trim()
        }
        await updateAppUser(editingDriver.id, { ...userData, role_id: driverForm.role_id })
        setSnackbar({ open: true, message: t("User updated successfully"), severity: "success" })
      } else {
        const userData = {
          name: `${driverForm.first_name.trim()} ${driverForm.last_name.trim()}`.trim(),
          first_name: driverForm.first_name.trim(),
          last_name: driverForm.last_name.trim(),
          email: driverForm.email.trim(),
          phone: driverForm.phone.trim(),
          password: driverForm.password.trim(),
          is_active: true,
        }
        await createAppUser({ ...userData, role_id: driverForm.role_id })
        setSnackbar({ open: true, message: t("User created successfully"), severity: "success" })
      }
      handleCloseDriverDialog()
      fetchDrivers()
    } catch (err) {
      console.error("Error saving user:", err)
      setSnackbar({ open: true, message: err.message || t("Error saving user"), severity: "error" })
    }
    setDriverSaving(false)
  }

  const handleDeleteDriver = async (driver) => {
    if (!window.confirm(t("Are you sure you want to delete this user?"))) return
    try {
      await deleteAppUser(driver.id)
      setSnackbar({ open: true, message: t("User deleted successfully"), severity: "success" })
      fetchDrivers()
    } catch (err) {
      console.error("Error deleting user:", err)
      setSnackbar({ open: true, message: err.message || t("Error deleting user"), severity: "error" })
    }
  }

  // Vehicles handlers
  const fetchVehicles = async () => {
    setVehiclesLoading(true)
    try {
      const res = await getVehicles().catch(() => [])
      setVehicles(res || [])
    } catch (err) {
      console.error("Error fetching vehicles:", err)
    }
    setVehiclesLoading(false)
  }

  const handleOpenVehicleDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle)
      setVehicleForm({
        make: vehicle.make || "",
        model: vehicle.model || "",
        year: vehicle.year || "",
        license_plate: vehicle.license_plate || "",
      })
    } else {
      setEditingVehicle(null)
      setVehicleForm({ make: "", model: "", year: "", license_plate: "" })
    }
    setVehicleDialogOpen(true)
  }

  const handleCloseVehicleDialog = () => {
    setVehicleDialogOpen(false)
    setEditingVehicle(null)
    setVehicleForm({ make: "", model: "", year: "", license_plate: "" })
  }

  const handleSaveVehicle = async () => {
    if (!vehicleForm.license_plate.trim()) {
      setSnackbar({ open: true, message: t("License plate is required"), severity: "error" })
      return
    }
    setVehicleSaving(true)
    try {
      if (editingVehicle) {
        await patchVehicle({
          resource: [{
            id: editingVehicle.id,
            make: vehicleForm.make.trim(),
            model: vehicleForm.model.trim(),
            year: vehicleForm.year.trim(),
            license_plate: vehicleForm.license_plate.trim(),
          }],
        })
        setSnackbar({ open: true, message: t("Vehicle updated successfully"), severity: "success" })
      } else {
        await postVehicle({
          resource: [{
            make: vehicleForm.make.trim(),
            model: vehicleForm.model.trim(),
            year: vehicleForm.year.trim(),
            license_plate: vehicleForm.license_plate.trim(),
            status: "active",
          }],
        })
        setSnackbar({ open: true, message: t("Vehicle added successfully"), severity: "success" })
      }
      handleCloseVehicleDialog()
      fetchVehicles()
    } catch (err) {
      console.error("Error saving vehicle:", err)
      setSnackbar({ open: true, message: t("Error saving vehicle"), severity: "error" })
    }
    setVehicleSaving(false)
  }

  const handleDeleteVehicle = async (vehicle) => {
    if (!window.confirm(t("Are you sure you want to delete this vehicle?"))) return
    try {
      await deleteVehicle({ resource: [{ id: vehicle.id }] })
      setSnackbar({ open: true, message: t("Vehicle deleted successfully"), severity: "success" })
      fetchVehicles()
    } catch (err) {
      console.error("Error deleting vehicle:", err)
      setSnackbar({ open: true, message: t("Error deleting vehicle"), severity: "error" })
    }
  }

  // Sections handlers
  const fetchSections = async () => {
    setSectionsLoading(true)
    try {
      const res = await getTypes().catch(() => [])
      setSections(res || [])
    } catch (err) {
      console.error("Error fetching sections:", err)
    }
    setSectionsLoading(false)
  }

  const handleOpenSectionDialog = (section = null) => {
    if (section) {
      setEditingSection(section)
      setSectionForm({
        name: section.name || "",
        description: section.description || "",
        floor: section.floor || "",
      })
    } else {
      setEditingSection(null)
      setSectionForm({ name: "", description: "", floor: "" })
    }
    setSectionDialogOpen(true)
  }

  const handleCloseSectionDialog = () => {
    setSectionDialogOpen(false)
    setEditingSection(null)
    setSectionForm({ name: "", description: "", floor: "" })
  }

  const handleSaveSection = async () => {
    if (!sectionForm.name.trim()) {
      setSnackbar({ open: true, message: t("Name is required"), severity: "error" })
      return
    }
    setSectionSaving(true)
    try {
      if (editingSection) {
        await updateTaskType({
          resource: [{
            id: editingSection.id,
            description: sectionForm.description.trim(),
            floor: sectionForm.floor,
          }],
        })
        setSnackbar({ open: true, message: t("Section updated successfully"), severity: "success" })
      } else {
        await createTaskType({
          resource: [{
            name: sectionForm.name.trim(),
            description: sectionForm.description.trim(),
            floor: sectionForm.floor,
          }],
        })
        setSnackbar({ open: true, message: t("Section added successfully"), severity: "success" })
      }
      handleCloseSectionDialog()
      fetchSections()
    } catch (err) {
      console.error("Error saving section:", err)
      setSnackbar({ open: true, message: t("Error saving section"), severity: "error" })
    }
    setSectionSaving(false)
  }

  const handleDeleteSection = async (section) => {
    if (!window.confirm(t("Are you sure you want to delete this section?"))) return
    try {
      await deleteTaskType({ resource: [{ id: section.id }] })
      setSnackbar({ open: true, message: t("Section deleted successfully"), severity: "success" })
      fetchSections()
    } catch (err) {
      console.error("Error deleting section:", err)
      setSnackbar({ open: true, message: t("Error deleting section"), severity: "error" })
    }
  }

  return (
    <Box sx={{ bgcolor: "#f9fafb", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Card elevation={0} sx={{ mx: 2, borderRadius: 2, border: "1px solid #e5e7eb", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 0.5,
                minHeight: 34,
                py: 0.25,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.78rem",
                  minHeight: 32,
                  py: 0.25,
                  px: 1,
                  borderRadius: 1,
                  mx: 0.25,
                  color: "#64748b",
                  transition: "all 0.15s ease",
                },
                "& .Mui-selected": {
                  color: "#fff !important",
                  bgcolor: "#3b82f6",
                  fontWeight: 600,
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              <Tab icon={<BusinessIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Company")} />
              <Tab icon={<PeopleIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Users")} />
              <Tab icon={<GroupIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Roles")} />
              <Tab icon={<DirectionsCarIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Vehicles")} />
              <Tab icon={<FactoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Sections")} />
              <Tab icon={<CategoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Categories")} />
              <Tab icon={<PaletteIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Swatches")} />
              <Tab icon={<UploadFileIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Catalog Update")} />
              <Tab icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Cost Designer")} />
              <Tab icon={<StraightenIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t("Custom Sizes")} />
            </Tabs>
          </Box>

          {/* Company Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ px: 2, pb: 2 }}>
              <CompanyTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Users Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ px: 2, pb: 2 }}>
              <UsersTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Roles & Permissions Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ px: 2, pb: 2 }}>
              <RolesTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Vehicles Tab */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ px: 2, pb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ width: 40, height: 40, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                    <DirectionsCarIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Company Vehicles")}
                    </Typography>
                    <Typography variant="body2" color="#6b7280">
                      {t("Manage company vehicles")}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenVehicleDialog()}
                  sx={{
                    bgcolor: "#3b82f6",
                    "&:hover": { bgcolor: "#2563eb" },
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  {t("Add Vehicle")}
                </Button>
              </Stack>

              {vehiclesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("License Plate")}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Make")}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Model")}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Year")}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>{t("Actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vehicles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#6b7280" }}>
                            {t("No vehicles found")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        vehicles.map((vehicle) => (
                          <TableRow key={vehicle.id} hover>
                            <TableCell>
                              <Chip label={vehicle.license_plate} size="small" sx={{ fontWeight: 600 }} />
                            </TableCell>
                            <TableCell>{vehicle.make}</TableCell>
                            <TableCell>{vehicle.model}</TableCell>
                            <TableCell>{vehicle.year}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => handleOpenVehicleDialog(vehicle)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteVehicle(vehicle)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          {/* Sections Tab */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ px: 2, pb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ width: 40, height: 40, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                    <FactoryIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Production Sections")}
                    </Typography>
                    <Typography variant="body2" color="#6b7280">
                      {t("Manage production sections and floors")}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenSectionDialog()}
                  sx={{
                    bgcolor: "#3b82f6",
                    "&:hover": { bgcolor: "#2563eb" },
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  {t("Add Section")}
                </Button>
              </Stack>

              {sectionsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Name")}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Description")}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Floor")}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>{t("Actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sections.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#6b7280" }}>
                            {t("No sections found")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        sections.map((section) => (
                          <TableRow key={section.id} hover>
                            <TableCell>
                              <Typography fontWeight={500}>{section.name}</Typography>
                            </TableCell>
                            <TableCell>{section.description || "-"}</TableCell>
                            <TableCell>
                              {section.floor ? (
                                <Chip
                                  label={FLOOR_OPTIONS.find(f => f.value === section.floor)?.label || section.floor}
                                  size="small"
                                  sx={{ bgcolor: "#e0e7ff", color: "#4338ca" }}
                                />
                              ) : "-"}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => handleOpenSectionDialog(section)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteSection(section)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          {/* Categories Tab */}
          <TabPanel value={activeTab} index={5}>
            <Box sx={{ px: 2, pb: 2 }}>
              <CategoriesTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Swatches Tab */}
          <TabPanel value={activeTab} index={6}>
            <Box sx={{ px: 2, pb: 2 }}>
              <SwatchesTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Catalog Update Tab */}
          <TabPanel value={activeTab} index={7}>
            <Box sx={{ px: 2, pb: 2 }}>
              <CatalogUpdateTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Cost Designer Tab */}
          <TabPanel value={activeTab} index={8}>
            <Box sx={{ px: 2, pb: 2 }}>
              <CostDesignerTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>

          {/* Custom Sizes Tab */}
          <TabPanel value={activeTab} index={9}>
            <Box sx={{ px: 2, pb: 2 }}>
              <CustomSizesTab onSnackbar={setSnackbar} />
            </Box>
          </TabPanel>
        </Card>

        {/* User Dialog */}
        <Dialog open={driverDialogOpen} onClose={handleCloseDriverDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
            {editingDriver ? t("Edit User") : t("Add User")}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={t("First Name")}
                value={driverForm.first_name}
                onChange={(e) => setDriverForm(prev => ({ ...prev, first_name: e.target.value }))}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Last Name")}
                value={driverForm.last_name}
                onChange={(e) => setDriverForm(prev => ({ ...prev, last_name: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Email")}
                type="email"
                value={driverForm.email}
                onChange={(e) => setDriverForm(prev => ({ ...prev, email: e.target.value }))}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Phone")}
                value={driverForm.phone}
                onChange={(e) => setDriverForm(prev => ({ ...prev, phone: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <FormControl fullWidth size="small" required>
                <InputLabel>{t("Role")}</InputLabel>
                <Select
                  value={driverForm.role_id}
                  label={t("Role")}
                  onChange={(e) => setDriverForm(prev => ({ ...prev, role_id: e.target.value }))}
                  sx={{ borderRadius: 2 }}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label={editingDriver ? t("New Password (leave empty to keep current)") : t("Password")}
                type="password"
                value={driverForm.password}
                onChange={(e) => setDriverForm(prev => ({ ...prev, password: e.target.value }))}
                required={!editingDriver}
                helperText={editingDriver ? t("Only fill if you want to change the password") : t("Password for user login")}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
            <Button onClick={handleCloseDriverDialog}>{t("Cancel")}</Button>
            <Button
              variant="contained"
              onClick={handleSaveDriver}
              disabled={driverSaving}
              sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
            >
              {driverSaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Vehicle Dialog */}
        <Dialog open={vehicleDialogOpen} onClose={handleCloseVehicleDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
            {editingVehicle ? t("Edit Vehicle") : t("Add Vehicle")}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={t("License Plate")}
                value={vehicleForm.license_plate}
                onChange={(e) => setVehicleForm(prev => ({ ...prev, license_plate: e.target.value }))}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Make")}
                value={vehicleForm.make}
                onChange={(e) => setVehicleForm(prev => ({ ...prev, make: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Model")}
                value={vehicleForm.model}
                onChange={(e) => setVehicleForm(prev => ({ ...prev, model: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Year")}
                value={vehicleForm.year}
                onChange={(e) => setVehicleForm(prev => ({ ...prev, year: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
            <Button onClick={handleCloseVehicleDialog}>{t("Cancel")}</Button>
            <Button
              variant="contained"
              onClick={handleSaveVehicle}
              disabled={vehicleSaving}
              sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
            >
              {vehicleSaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Section Dialog */}
        <Dialog open={sectionDialogOpen} onClose={handleCloseSectionDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
            {editingSection ? t("Edit Section") : t("Add Section")}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={t("Name")}
                value={sectionForm.name}
                onChange={(e) => setSectionForm(prev => ({ ...prev, name: e.target.value }))}
                required
                disabled={!!editingSection}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Description")}
                value={sectionForm.description}
                onChange={(e) => setSectionForm(prev => ({ ...prev, description: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                select
                label={t("Floor")}
                value={sectionForm.floor}
                onChange={(e) => setSectionForm(prev => ({ ...prev, floor: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                <MenuItem value="">{t("Select Floor")}</MenuItem>
                {FLOOR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
            <Button onClick={handleCloseSectionDialog}>{t("Cancel")}</Button>
            <Button
              variant="contained"
              onClick={handleSaveSection}
              disabled={sectionSaving}
              sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
            >
              {sectionSaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  )
}

export default MuiSettingsConfiguration
